<template>
  <div class="txn-status-banner" :class="statusClass">
    <div class="icon-wrap" :class="iconWrapClass">
      <BaseSvgIcon
        :name="iconName"
        class="status-icon"
        :class="iconColorClass"
        viewBox="0 0 20 20"
      />
    </div>
    <div class="content">
      <div class="title">{{ resolvedTitle }}</div>
      <div class="subtitle">{{ resolvedSubtitle }}</div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { BaseSvgIcon } from "@/shared/ui/BaseSvgIcon";
import "@/assets/icons/SVG/pending_instant_link.svg";
import "@/assets/icons/SVG/close2.svg";

const props = defineProps({
  status: {
    type: String,
    default: "pending",
    validator: (value) => ["pending", "failed"].includes(value),
  },
  title: {
    type: String,
    default: "",
  },
  subtitle: {
    type: String,
    default: "",
  },
});

const { t } = useI18n();

const isPending = computed(() => props.status === "pending");
const isFailure = computed(() => props.status === "failed");

const iconName = computed(() =>
  isPending.value ? "pending_instant_link" : "close2",
);

const statusClass = computed(() => ({
  pending: isPending.value,
  failure: isFailure.value,
}));

const iconWrapClass = computed(() =>
  isPending.value ? "bg-pending" : "bg-failure",
);
const iconColorClass = computed(() =>
  isPending.value ? "color-pending" : "color-failure",
);

const resolvedTitle = computed(() => {
  if (props.title) return props.title;
  if (isPending.value) {
    return t("explorer.instant_link_banner.titlePending");
  } else {
    return t("explorer.instant_link_banner.titleFail");
  }
});

const resolvedSubtitle = computed(() => {
  if (props.subtitle) return props.subtitle;
  if (isPending.value) {
    return t("explorer.instant_link_banner.subtitlePending");
  } else {
    return t("explorer.instant_link_banner.subtitleFail");
  }
});
</script>

<style lang="scss" scoped>
.txn-status-banner {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px 32px;
  background: #111111;
  border: 1px solid #1a1a1a;
  border-radius: 10px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 24px;
  }
}

.icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14px;
  border-radius: 58px;
}

.bg-pending {
  background: #221601;
}
.bg-failure {
  background: #350a09;
}

.status-icon {
  width: 20px;
  height: 20px;
}

.color-pending {
  color: #f59e0b;
}
.color-failure {
  color: #e53935;
}

.content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.title {
  color: #fafafa;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
}

.subtitle {
  color: #7a7a7a;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
}
</style>
